/**
 * Typed API client. Response shapes come from `@dcc/shared`, so a contract
 * change breaks the build rather than a screen at runtime.
 */
import type {
  AgentOpsView,
  ApiError,
  AuthenticatedUser,
  CrewView,
  JourneyView,
  PortfolioView,
  ProjectDetailView,
} from '@dcc/shared';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string;
  readonly details: { field: string; message: string }[] | undefined;

  constructor(status: number, payload: ApiError['error']) {
    super(payload.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = payload.code;
    this.correlationId = payload.correlationId;
    this.details = payload.details;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `init` is spread first: spreading it last would overwrite the merged
  // headers with the caller's, which silently dropped `Content-Type` on every
  // command that carries an idempotency key.
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let payload: ApiError['error'] = {
      code: 'NETWORK',
      message: 'The request could not be completed.',
      correlationId: 'unknown',
    };
    try {
      const body = (await response.json()) as ApiError;
      if (body?.error) payload = body.error;
    } catch {
      /* keep the safe default */
    }
    throw new ApiRequestError(response.status, payload);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface Command<T = unknown> {
  ok: boolean;
  resourceVersion?: number;
  auditEventId?: string | null;
  idempotentReplay?: boolean;
  data?: T;
  reason?: string;
}

function post<T>(path: string, body: unknown, idempotencyKey?: string): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
}

export interface AgentKeySummary {
  id: string;
  label: string;
  projectId: string | null;
  prefix: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
  revokedAt: string | null;
  revokedReason: string | null;
}

export interface IssuedAgentKey {
  keyId: string;
  label: string;
  projectId: string | null;
  prefix: string;
  expiresAt: string | null;
  /** Present exactly once, in the response that created it. */
  key: string;
  notice: string;
}

export interface UserSummary {
  id: string;
  email: string;
  displayName: string;
  role: string;
  memberId: string | null;
  createdAt: string;
  disabledAt: string | null;
  passwordSetAt: string | null;
  invitedBy: string | null;
  activeSessions: number;
  lastSignInAt: string | null;
}

export interface InviteResult {
  userId: string;
  email?: string;
  expiresAt: string;
  /** Shown once. There is no way to retrieve it afterwards. */
  setPasswordPath: string;
  notice?: string;
}

export const api = {
  login: (email: string, password: string) =>
    post<{ user: AuthenticatedUser; expiresAt: string }>('/api/v1/auth/login', { email, password }),
  logout: () => post<{ ok: boolean }>('/api/v1/auth/logout', {}),
  me: () => request<{ user: AuthenticatedUser }>('/api/v1/auth/me'),

  portfolio: () => request<PortfolioView>('/api/v1/portfolio'),
  project: (id: string) => request<ProjectDetailView>(`/api/v1/projects/${id}`),
  journey: (id: string, asOf?: string | null) =>
    request<JourneyView>(
      `/api/v1/projects/${id}/journey${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`,
    ),
  crew: () => request<CrewView>('/api/v1/crew'),
  agents: () => request<AgentOpsView>('/api/v1/agents'),
  connections: (id: string) =>
    request<{ connections: ProjectDetailView['connections']; discovery: unknown }>(
      `/api/v1/projects/${id}/connections`,
    ),
  users: () => request<{ users: UserSummary[] }>('/api/v1/users'),
  inviteUser: (email: string, displayName: string, role: string) =>
    post<{ ok: boolean; data: InviteResult }>('/api/v1/users', { email, displayName, role }),
  resetUserPassword: (userId: string) =>
    post<{ ok: boolean; data: InviteResult }>(`/api/v1/users/${userId}/reset-password`, {}),
  setUserRole: (userId: string, role: string) =>
    post<Command>(`/api/v1/users/${userId}/role`, { role }),
  disableUser: (userId: string) => post<Command>(`/api/v1/users/${userId}/disable`, {}),
  enableUser: (userId: string) => post<Command>(`/api/v1/users/${userId}/enable`, {}),
  revokeUserSessions: (userId: string) =>
    post<Command>(`/api/v1/users/${userId}/sessions/revoke`, {}),
  setPassword: (token: string, password: string) =>
    post<{ ok: boolean; data: { email: string } }>('/api/v1/auth/password/set', {
      token,
      password,
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<Command>('/api/v1/auth/password', { currentPassword, newPassword }),

  agentKeys: () => request<{ keys: AgentKeySummary[] }>('/api/v1/agent-keys'),
  issueAgentKey: (label: string, projectId: string | null) =>
    post<{ ok: boolean; data: IssuedAgentKey }>('/api/v1/agent-keys', { label, projectId }),
  revokeAgentKey: (keyId: string, reason: string) =>
    post<Command>(`/api/v1/agent-keys/${keyId}/revoke`, { reason }),

  audit: (limit = 60) =>
    request<{ audit: Record<string, unknown>[] }>(`/api/v1/audit?limit=${limit}`),
  metrics: () => request<Record<string, unknown>>('/api/v1/metrics'),

  resolveDecision: (
    projectId: string,
    decisionId: string,
    optionKey: string,
    reason: string,
    key: string,
  ) =>
    post<Command>(
      `/api/v1/projects/${projectId}/decisions/${decisionId}/resolve`,
      { optionKey, reason },
      key,
    ),
  resolveBlocker: (
    projectId: string,
    blockerId: string,
    resolution: 'RESOLVED' | 'MITIGATED',
    reason: string,
    key: string,
  ) =>
    post<Command>(
      `/api/v1/projects/${projectId}/blockers/${blockerId}/resolve`,
      { resolution, reason },
      key,
    ),
  approveStation: (
    projectId: string,
    nodeId: string,
    decision: 'APPROVED' | 'REJECTED' | 'DEFERRED',
    reason: string,
    key: string,
  ) =>
    post<Command>(
      `/api/v1/projects/${projectId}/journey/nodes/${nodeId}/approval`,
      { decision, reason },
      key,
    ),
  createHandoff: (
    projectId: string,
    workstream: string,
    toMemberId: string,
    reason: string,
    key: string,
  ) =>
    post<Command & { checks: { name: string; ok: boolean; detail: string }[]; note: string }>(
      '/api/v1/handoffs',
      { projectId, workstream, toMemberId, reason },
      key,
    ),
  createProject: (name: string, slug: string, key: string) =>
    post<Command & { projectId: string; nextStep: string }>(
      '/api/v1/projects',
      { name, slug },
      key,
    ),
  bootstrap: (projectId: string, plan: unknown, key: string) =>
    post<BootstrapResult>(`/api/v1/projects/${projectId}/bootstrap`, plan, key),
  discover: (projectId: string, installationId: string, repositories: string[], key: string) =>
    post<DiscoveryResult>(
      `/api/v1/projects/${projectId}/connections/discover`,
      { provider: 'github', installationId, repositories },
      key,
    ),
  confirmMapping: (
    projectId: string,
    connectionId: string,
    mappings: MappingDecision[],
    reason: string,
    key: string,
  ) =>
    post<ConfirmResult>(
      `/api/v1/projects/${projectId}/connections/${connectionId}/confirm`,
      { mappings, reason },
      key,
    ),
  validateManifest: (projectId: string, manifest: string) =>
    post<ManifestValidation>(`/api/v1/projects/${projectId}/manifest/validate`, { manifest }),
  healthCheck: (projectId: string, connectionId: string) =>
    post<{
      checks: {
        key: string;
        label: string;
        mandatory: boolean;
        status: string;
        detail: string;
        remediation: string | null;
      }[];
      summary: { state: string; detail: string };
      checkedAt: string;
    }>(`/api/v1/projects/${projectId}/connections/${connectionId}/health-check`, {}),
};

export interface DiscoveryFinding {
  key: string;
  category: string;
  fact: string;
  evidencePath: string;
  confidence: number;
  quality: string;
}

export interface MappingProposal {
  detectedKey: string;
  entityType: 'service' | 'environment' | 'repository' | 'source_of_truth';
  detectedValue: string;
  proposedValue: string;
  confidence: number;
  evidencePath: string;
  requiresConfirmation: boolean;
  reason: string;
}

export interface DiscoveryResult {
  ok: boolean;
  discoveryId: string;
  connectionId: string;
  provider: string;
  quality: string;
  repositories: { providerRepoId: string; owner: string; name: string; defaultBranch: string }[];
  findings: DiscoveryFinding[];
  proposals: MappingProposal[];
  unsupported: string[];
}

export interface MappingDecision {
  detectedKey: string;
  entityType: MappingProposal['entityType'];
  confirmedValue: string;
  accepted: boolean;
}

export interface VerificationCheck {
  key: string;
  label: string;
  mandatory: boolean;
  status: 'PASS' | 'FAIL' | 'SKIPPED' | 'UNKNOWN';
  detail: string;
  remediation: string | null;
}

export interface ConfirmResult {
  ok: boolean;
  state: string;
  detail: string;
  verification: VerificationCheck[];
}

export interface ManifestValidation {
  valid: boolean;
  issues: { path: string; message: string; severity: 'ERROR' | 'WARNING' }[];
  parsed: Record<string, unknown> | null;
}

export interface BootstrapResult {
  ok: boolean;
  preview: boolean;
  manifest: string;
  validation: ManifestValidation;
  externalWrites: { target: string; action: string; detail: string }[];
  note?: string;
  nextStep?: string;
}

export function idempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}
